import { useState, type ComponentProps } from "react";
import { Box } from "@mui/material";
import OfferApplicantForm from "@components/ui/offer-applicant/form";
import ApplicationStatus from "@components/ui/offer-applicant/ApplicationStatus";
import OfferResponse from "./OfferResponse";
import { submitOfferDecision } from "@services/api/offer-applicant/submitOfferDecision/submitOfferDecision";
import { OfferApplicantStatus } from "./../../../types/OfferApplicantStatus.interface";
import { enqueueSnackbar } from "notistack";
import { useLanguage } from "./../../../contexts/LanguageContext";
import translations from "./ApplicationInformation.translations.json";

type SupportedLanguage = keyof typeof translations;

type OfferApplicantFormProps = ComponentProps<typeof OfferApplicantForm>;

interface ApplicationInformationProps extends OfferApplicantFormProps {
    status: ComponentProps<typeof ApplicationStatus>["status"];
    offerId: number;
    onRefreshStatus: (status: OfferApplicantStatus) => void;
}

const ApplicationInformation = ({
    status,
    offerId,
    onRefreshStatus,
    ...formProps
}: ApplicationInformationProps) => {

    const { language } = useLanguage();
    const content = translations[language as SupportedLanguage] || translations.en;
    const [loading, setLoading] = useState<boolean>(false);

    const handleAction = async (action: "accept" | "reject") => {
        setLoading(true);
        try {
            if (action === "accept") {
                const response = await submitOfferDecision(OfferApplicantStatus.OFFER_ACCEPTED_BY_CANDIDATE);
                if (response?.data?.submitted) {
                    enqueueSnackbar(content.decisionSaved, { variant: "success" });
                    onRefreshStatus(OfferApplicantStatus.OFFER_ACCEPTED_BY_CANDIDATE);
                } else {
                    enqueueSnackbar(content.decisionFailed, { variant: "error" });
                }
            
            } else {
                const response = await submitOfferDecision(OfferApplicantStatus.OFFER_REJECTED_BY_CANDIDATE);
                if (response?.data?.submitted) {
                    enqueueSnackbar(content.decisionSaved, { variant: "success" });
                    onRefreshStatus(OfferApplicantStatus.OFFER_REJECTED_BY_CANDIDATE);
                } else {
                    enqueueSnackbar(content.decisionFailed, { variant: "error" });
                }
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <ApplicationStatus status={status} />
            <OfferResponse
                status={status}
                loading={loading}
                onAccept={() => handleAction("accept")}
                onReject={() => handleAction("reject")}
            />
            <OfferApplicantForm {...formProps} />
        </Box>
    );
};

export default ApplicationInformation;
